import React from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { BsList } from "react-icons/bs";

const NAV_GROUPS = [
  { items: [{ to: "/admin/welcome", label: "Today" }] },
  {
    title: "Bookings",
    items: [
      { to: "/admin/appointments", label: "Appointments" },
      { to: "/admin/astro", label: "Astro" },
      { to: "/admin/Events", label: "Events" },
      { to: "/admin/kashiyatra", label: "Kashiyatra" },
      { to: "/admin/submissions", label: "Submissions" },
    ],
  },
  {
    title: "People",
    items: [
      { to: "/admin/users", label: "Users" },
      { to: "/admin/birthday", label: "Birthdays" },
      { to: "/admin/occupation", label: "Occupations" },
      { to: "/admin/employee", label: "Employees" },
    ],
  },
  { title: "Money", items: [{ to: "/admin/revenue", label: "Revenue" }] },
  {
    title: "Website",
    items: [
      { to: "/admin/gallery", label: "Gallery" },
      { to: "/admin/youtube", label: "YouTube" },
    ],
  },
];

const RAIL_WIDTH = "232px";

const LOGO_ALT = "Datta Sai Astro & Priest Service";

// The logo's reds and oranges disappear on kumkum, so it sits on a white label.
function BrandLabel() {
  return (
    <Flex bg="white" justify="center" px={4} py={4} flexShrink={0}>
      <Image src={`${process.env.PUBLIC_URL}/logo-full.png`} alt={LOGO_ALT} w="132px" />
    </Flex>
  );
}

function BrandBar() {
  return (
    <Flex align="center" gap={3}>
      <Flex bg="white" borderRadius="md" p={1}>
        <Image src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="" boxSize="36px" objectFit="contain" />
      </Flex>
      <Text fontFamily="heading" fontSize="xl" lineHeight="1.1" color="white">
        Datta Sai
      </Text>
    </Flex>
  );
}

function NavItems({ onNavigate, onLogout }) {
  return (
    <Flex direction="column" flex="1" minH={0}>
      <Box as="nav" aria-label="Main" flex="1" overflowY="auto" px={3} pb={4} sx={{ scrollbarWidth: "thin", scrollbarColor: "#A83F52 transparent" }}>
        {NAV_GROUPS.map((group, index) => (
          <Box key={group.title || index} mt={index === 0 ? 0 : 4}>
            {group.title && (
              <Text px={3} mb={1} fontSize="sm" color="kumkum.200">
                {group.title}
              </Text>
            )}
            {group.items.map((item) => (
              <Box
                key={item.to}
                as={NavLink}
                to={item.to}
                onClick={onNavigate}
                display="block"
                px={3}
                py={1.5}
                borderRadius="md"
                color="white"
                fontWeight={500}
                _hover={{ bg: "kumkum.600" }}
                sx={{ "&.active": { bg: "turmeric.500", color: "ink", fontWeight: 600 } }}
                _focusVisible={{ outlineColor: "turmeric.500" }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box px={3} py={3} borderTop="1px solid" borderColor="kumkum.400">
        <Button
          variant="unstyled"
          width="100%"
          textAlign="left"
          px={3}
          color="white"
          fontWeight={500}
          onClick={onLogout}
          _hover={{ bg: "kumkum.600" }}
          _focusVisible={{ outlineColor: "turmeric.500" }}
        >
          Log out
        </Button>
      </Box>
    </Flex>
  );
}

function AppShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const token = localStorage.getItem("AdminToken");

  if (!token || pathname === "/") {
    return children;
  }

  const logout = () => {
    localStorage.removeItem("AdminToken");
    onClose();
    navigate("/");
  };

  return (
    <Flex minH="100vh" align="stretch">
      <Flex
        as="aside"
        display={{ base: "none", lg: "flex" }}
        direction="column"
        position="sticky"
        top={0}
        h="100vh"
        w={RAIL_WIDTH}
        flexShrink={0}
        bg="kumkum.500"
      >
        <BrandLabel />
        <Box h={4} flexShrink={0} />
        <NavItems onLogout={logout} />
      </Flex>

      <Box flex="1" minW={0}>
        <Flex
          display={{ base: "flex", lg: "none" }}
          align="center"
          justify="space-between"
          position="sticky"
          top={0}
          zIndex="sticky"
          bg="kumkum.500"
          px={4}
          py={2}
        >
          <BrandBar />
          <IconButton
            aria-label="Open menu"
            icon={<BsList size={24} />}
            variant="unstyled"
            display="flex"
            color="white"
            onClick={onOpen}
            _focusVisible={{ outlineColor: "turmeric.500" }}
          />
        </Flex>
        <Box as="main" maxW="1240px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 5, md: 8 }}>
          {children}
        </Box>
      </Box>

      <Drawer isOpen={isOpen} onClose={onClose} placement="left" size="xs">
        <DrawerOverlay />
        <DrawerContent bg="kumkum.500">
          <DrawerBody p={0} display="flex" flexDirection="column">
            <BrandLabel />
            <Box h={4} flexShrink={0} />
            <NavItems onNavigate={onClose} onLogout={logout} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default AppShell;
